import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useMutation, useQuery } from "@tanstack/react-query";
import { Check, CreditCard, Sparkles } from "lucide-react";
import { isUnlimited, type PlanKey } from "@marketing/shared";
import { api, apiErrorMessage, unwrap } from "@/lib/api";
import { Card, Badge, Notice, PageHeader, Spinner } from "@/components/ui";
import { ErrorBox } from "./LoginPage";

// =============================================================================
// Plans, and the doubling.
//
// Every number on this screen comes from the API, which reads it from PLANS —
// including `previousPostsPerMonth`, the figure each tier used to allow. A
// marketing claim typed into this component would be a claim that goes stale
// the first time a limit moves, and an overstated allowance is the kind of
// stale that gets noticed by the person who hits the real limit.
//
// A tier whose Paystack plan has not been created yet arrives with
// `sellable: false` and shows as "coming soon" rather than offering a button
// that fails the moment it is pressed.
//
// Paying leaves this page entirely, for Paystack's hosted checkout. That is
// where mobile money, bank transfer and USSD live — which is how most of this
// product's customers actually pay — and it behaves on the low-end Android
// browsers they use. An inline popup would mean loading a payment SDK on
// every visit for the sake of staying on our own domain.
// =============================================================================

type PlanRow = {
  key: PlanKey;
  name: string;
  postsPerMonth: number;
  previousPostsPerMonth: number | null;
  maxBrands: number;
  maxPages: number;
  watermark: boolean;
  teamRoles: boolean;
  sellable: boolean;
  /**
   * What it costs, per interval, formatted — read from Paystack rather than
   * from our own configuration, so the figure on screen is the figure that
   * gets charged. Null when no plan exists for that interval, or when
   * Paystack could not be reached.
   */
  prices: Record<string, string | null>;
  /**
   * The intervals this plan can be bought on.
   *
   * Checked as well as "sellable", which is true when ANY interval has a plan
   * code — so a monthly-only plan was sellable while its annual button could
   * only ever fail.
   */
  intervals: Array<"monthly" | "annual">;
};

type BillingData = {
  current: {
    plan: PlanKey;
    interval: "monthly" | "annual";
    status: string | null;
    currentPeriodEnd: string | null;
  };
  plans: PlanRow[];
  packs: Array<{ key: string; posts: number; price: string | null; sellable: boolean }>;
  currency: string;
  /** Intervals with at least one Paystack plan behind them. */
  intervals: Array<"monthly" | "annual">;
  checkoutReady: boolean;
  /** False on a test key, so a sandbox in production is visible on screen. */
  live: boolean;
};

export function BillingPage() {
  const { t } = useTranslation();
  const [interval, setInterval] = useState<"monthly" | "annual">("monthly");
  const [error, setError] = useState<string | null>(null);

  const billing = useQuery({
    queryKey: ["billing"],
    queryFn: () => unwrap<BillingData>(api.get("/billing/plans")),
  });

  /**
   * Keeps the selected interval to one that can actually be bought.
   *
   * The default is monthly, which is right for every configuration that sells
   * monthly. It is wrong for one that sells only annually: the switch is
   * hidden when there is a single option, so monthly would stay selected,
   * every plan would fail its interval check, and the whole page would read
   * "coming soon" with no way to correct it.
   */
  const offered = billing.data?.intervals;
  useEffect(() => {
    if (offered && offered.length > 0 && !offered.includes(interval)) {
      setInterval(offered[0]!);
    }
  }, [offered, interval]);

  const checkout = useMutation({
    mutationFn: async (what: { plan?: PlanKey; pack?: string }) => {
      const { authorizationUrl } = await unwrap<{ authorizationUrl: string }>(
        api.post("/billing/checkout", { ...what, interval }),
      );
      return authorizationUrl;
    },
    // A full navigation, not a new tab: pop-up blockers eat a window.open that
    // happens after an await, and this one always does — the URL only exists
    // once the server has initialised the payment.
    onSuccess: (url) => window.location.assign(url),
    onError: (err) => setError(apiErrorMessage(err, t("errors.generic"))),
  });

  if (billing.isLoading) return <p className="text-sm text-muted">{t("errors.loading")}</p>;
  const data = billing.data;
  if (!data) return <ErrorBox>{t("errors.generic")}</ErrorBox>;

  // A current plan the catalogue does not list is an internal grant. Derived
  // rather than compared against "unlimited" so a second internal tier needs
  // no change here, and because this is exactly the condition that makes the
  // cards wrong: no card matches, so every one of them offers to sell a
  // downgrade to someone who already has more than all of them.
  const internal = !data.plans.some((plan) => plan.key === data.current.plan);

  return (
    <div className="space-y-6">
      <PageHeader title={t("billing.title")} subtitle={t("billing.subtitle")} />

      {/* The offer, stated once at the top rather than on every card. */}
      <div className="flex items-start gap-3 rounded-xl border border-primary-200 bg-primary-50 px-4 py-3">
        <Sparkles className="mt-0.5 h-4 w-4 shrink-0 text-primary-700" aria-hidden />
        <div className="text-sm">
          <p className="font-medium text-primary-800">{t("billing.doubledTitle")}</p>
          <p className="text-primary-700">{t("billing.doubledBody")}</p>
        </div>
      </div>

      {internal ? (
        <Notice tone="info">
          <span className="font-medium">{t("billing.internalTitle")}</span>{" "}
          {t("billing.internalBody")}
        </Notice>
      ) : null}

      {!data.checkoutReady ? (
        <Notice tone="warning">{t("billing.notConfigured")}</Notice>
      ) : !data.live ? (
        // Otherwise a test key in production is discovered when the first real
        // payment silently does nothing.
        <Notice tone="warning">{t("billing.testMode")}</Notice>
      ) : null}
      {error ? <ErrorBox>{error}</ErrorBox> : null}

{/* Only intervals that can actually be bought, and no switch at all when
          there is nothing to switch between — which is the case while only
          monthly plans exist in Paystack. */}
      {data.intervals.length > 1 ? (
        <div className="flex items-center gap-2">
          {data.intervals.map((value) => (
            <button
              key={value}
              onClick={() => setInterval(value)}
              className={`rounded-lg px-3 py-1.5 text-sm font-medium ${
                interval === value
                  ? "bg-night-900 text-white"
                  : "border border-border text-muted hover:text-ink"
              }`}
            >
              {t(`billing.${value}`)}
            </button>
          ))}
        </div>
      ) : null}

      <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
        {data.plans.map((plan) => {
          const current = plan.key === data.current.plan;
          return (
            <Card key={plan.key} className={current ? "ring-2 ring-primary-500" : ""}>
              <div className="flex items-center justify-between">
                <h3 className="text-base font-semibold">{plan.name}</h3>
                {current ? <Badge tone="info">{t("billing.current")}</Badge> : null}
              </div>

              {/* Price first: it is the question someone opens this page
                  with. Free says so rather than showing nothing, and a plan
                  whose price could not be read shows the allowance alone
                  instead of a blank where a number should be. */}
              {plan.key === "free" ? (
                <p className="mt-2 text-2xl font-semibold">{t("billing.free")}</p>
              ) : plan.prices[interval] ? (
                <p className="mt-2 text-2xl font-semibold">
                  {plan.prices[interval]}
                  <span className="text-sm font-normal text-muted">
                    {interval === "monthly" ? t("billing.perMonth") : t("billing.perYear")}
                  </span>
                </p>
              ) : null}

              <p className="mt-3 text-base font-semibold">
                {isUnlimited(plan.postsPerMonth)
                  ? t("billing.unlimited")
                  : t("billing.postsPerMonth", { count: plan.postsPerMonth })}
              </p>
              {plan.previousPostsPerMonth ? (
                <p className="text-sm text-muted">
                  <span className="line-through">{plan.previousPostsPerMonth}</span>{" "}
                  {t("billing.wasBefore")}
                </p>
              ) : null}

              <ul className="mt-4 space-y-1.5 text-sm text-muted">
                <Feature>
                  {t("billing.brands", {
                    count: isUnlimited(plan.maxBrands) ? 0 : plan.maxBrands,
                  })}
                </Feature>
                <Feature>
                  {t("billing.pages", {
                    count: isUnlimited(plan.maxPages) ? 0 : plan.maxPages,
                  })}
                </Feature>
                {!plan.watermark ? <Feature>{t("billing.noWatermark")}</Feature> : null}
                {plan.teamRoles ? <Feature>{t("billing.teamRoles")}</Feature> : null}
              </ul>

              <div className="mt-5">
                {internal ? null : current ? (
                  <button disabled className="btn-secondary w-full opacity-60">
                    {t("billing.yourPlan")}
                  </button>
                ) : plan.key === "free" ? null : !plan.sellable ||
                  !plan.intervals.includes(interval) ? (
                  <button disabled className="btn-secondary w-full opacity-60">
                    {t("billing.comingSoon")}
                  </button>
                ) : (
                  <button
                    onClick={() => checkout.mutate({ plan: plan.key })}
                    disabled={checkout.isPending || !data.checkoutReady}
                    className="btn-primary w-full"
                  >
                    {checkout.isPending ? <Spinner /> : <CreditCard size={15} />}
                    {t("billing.choose", { name: plan.name })}
                  </button>
                )}
              </div>
            </Card>
          );
        })}
      </div>

      {/* Packs top up an allowance an internal plan never runs down, so
          this is money for nothing on a master account. */}
      {internal ? null : (
        <Card>
          <h3 className="text-base font-semibold">{t("billing.packsTitle")}</h3>
          <p className="mt-1 text-sm text-muted">{t("billing.packsBody")}</p>
          <div className="mt-4 flex flex-wrap gap-3">
            {data.packs.map((pack) => (
              <button
                key={pack.key}
                onClick={() => checkout.mutate({ pack: pack.key })}
                disabled={!pack.sellable || checkout.isPending || !data.checkoutReady}
                className="btn-secondary disabled:opacity-50"
              >
                {t("billing.buyPack", { count: pack.posts })}
                {pack.price ? <span className="ml-2 text-muted">{pack.price}</span> : null}
              </button>
            ))}
          </div>
        </Card>
      )}
    </div>
  );
}

function Feature({ children }: { children: React.ReactNode }) {
  return (
    <li className="flex items-start gap-2">
      <Check size={15} className="mt-0.5 shrink-0 text-success" aria-hidden />
      <span>{children}</span>
    </li>
  );
}
